'use client'

import React, { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft, faBookOpen } from "@fortawesome/free-solid-svg-icons"
import { useGlobalContext } from "../component/context/provider"
import { AxiosGetData } from "../component/context/axiosfunction"
import CardEbook from "../component/Card/Card"

const ColumnDetail = ({ columnId }) => {
    const router = useRouter()
    const { API, headers, privateKey } = useGlobalContext()
    const [columnData, setColumnData] = useState(null)
    const [books, setBooks] = useState([])
    const [recommendedBooks, setRecommendedBooks] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchColumnData = async () => {
            try {
                setLoading(true)
                const colRes = await AxiosGetData(API, `/api/column_detail/${columnId}`, headers, privateKey)

                if (colRes?.column_data) {
                    setColumnData(colRes.column_data)
                    const sortedBooks = [...(colRes.column_book ?? [])].sort((a, b) => {
                        if (a.sort_order != null && b.sort_order != null) {
                            return a.sort_order - b.sort_order
                        }
                        const orderA = Array.isArray(a.order_id) ? (a.order_id[0]?.order ?? 0) : 0
                        const orderB = Array.isArray(b.order_id) ? (b.order_id[0]?.order ?? 0) : 0
                        return orderA - orderB
                    })
                    setBooks(sortedBooks)
                }

                // ดึงหนังสือแนะนำเพิ่มเติมสำหรับส่วนล่าง
                try {
                    const homeRes = await AxiosGetData(API, '/api/home_page', headers, privateKey)
                    const allColumns = homeRes?.Column_data ?? []
                    let otherBooks = []
                    allColumns.forEach(colSet => {
                        if (!Array.isArray(colSet)) return
                        colSet.forEach(colObj => {
                            if (String(colObj?.column_data?.id) !== String(columnId)) {
                                otherBooks.push(...(colObj?.column_book ?? []))
                            }
                        })
                    })

                    const uniqueRecs = Array.from(new Set(otherBooks.map(b => b.id)))
                        .map(id => otherBooks.find(b => b.id === id))
                        .slice(0, 12)
                    setRecommendedBooks(uniqueRecs)
                } catch {
                    // Ignore recommendation fetch errors
                }

                setLoading(false)
            } catch (err) {
                console.error("Error fetching column data:", err)
                setLoading(false)
            }
        }

        if (columnId) {
            fetchColumnData()
        }
    }, [API, headers, privateKey, columnId])

    return (
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-8">
            {/* Header bar / Breadcrumb */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => router.back()}
                    className="flex items-center gap-2 text-primary hover:text-special transition-colors text-sm font-medium"
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    <span>ย้อนกลับ</span>
                </button>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <div className="w-12 h-12 border-4 border-[#EC008C] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-500 text-sm">กำลังโหลดข้อมูลคอลัมน์...</p>
                </div>
            ) : columnData ? (
                <>
                    {/* Section Header */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between pb-6 mb-8 border-b border-gray-200 gap-4">
                        <div className="flex items-center gap-3">
                            <img
                                src="https://eve-image.eveebook.com/image.eveebook/mascot/happy.png"
                                alt="happy_rabbit"
                                className="w-12 md:w-14"
                            />
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-2xl md:text-3xl font-bold text-primary">
                                        {columnData.order_name}
                                    </h1>
                                    {columnData.order_discount && (
                                        <span className="text-special text-2xl md:text-3xl font-semibold">
                                            {columnData.order_discount}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-500 mt-1">
                                    รวมทั้งหมด <span className="font-bold text-[#EC008C]">{books.length}</span> เล่ม
                                </p>
                            </div>
                        </div>

                        <Link
                            href="/"
                            className="text-sm text-gray-500 hover:text-primary transition-colors flex items-center gap-1 self-start md:self-auto"
                        >
                            <FontAwesomeIcon icon={faBookOpen} />
                            <span>กลับไปหน้าหลัก</span>
                        </Link>
                    </div>

                    {/* Book Grid */}
                    {books.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6 mb-16">
                            {books.map((book, index) => (
                                <CardEbook
                                    key={book.id || index}
                                    Ebookdata={book}
                                    type={columnData?.auto_key === 'search' ? 'SearchBook' : 'SeriesToBook'}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16 bg-gray-50 rounded-xl mb-12">
                            <p className="text-gray-500 text-base">ไม่พบรายการหนังสือในคอลัมน์นี้</p>
                        </div>
                    )}

                    {/* Recommended section */}
                    {recommendedBooks.length > 0 && (
                        <div className="pt-10 border-t border-gray-200">
                            <div className="flex items-center gap-2 mb-6">
                                <span className="w-2 h-6 bg-[#EC008C] rounded-full"></span>
                                <h2 className="text-xl md:text-2xl font-bold text-primary">
                                    หนังสือแนะนำเพิ่มเติมที่คุณอาจสนใจ
                                </h2>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
                                {recommendedBooks.map((book, index) => (
                                    <CardEbook
                                        key={book.id || index}
                                        Ebookdata={book}
                                        type="SeriesToBook"
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center py-20">
                    <p className="text-gray-500 text-lg mb-4">ไม่พบข้อมูลคอลัมน์ที่ต้องการ</p>
                    <Link
                        href="/"
                        className="inline-block px-6 py-2.5 bg-[#EC008C] text-white rounded-lg hover:bg-[#ce326b] transition-colors"
                    >
                        กลับสู่หน้าหลัก
                    </Link>
                </div>
            )}
        </div>
    )
}

export default ColumnDetail
